import React from 'react';
import { Loader2, Check, X, Search, Globe, BookOpen, FileText } from 'lucide-react';
import { StreamingToolCall } from '@/hooks/useStreamingChat';

interface LiveToolCallStreamProps {
  toolCalls: StreamingToolCall[];
}

const LiveToolCallStream: React.FC<LiveToolCallStreamProps> = ({ toolCalls }) => {

  if (toolCalls.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      {toolCalls.map((call, index) => (
        <ToolCallCard key={`${call.tool}-${index}`} call={call} />
      ))}
    </div>
  );
};

interface ToolCallCardProps {
  call: StreamingToolCall;
}

const ToolCallCard: React.FC<ToolCallCardProps> = ({ call }) => {
  const getStatusIcon = () => {
    switch (call.status) {
      case 'running':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'completed':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <X className="w-4 h-4 text-red-500" />;
      default:
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
    }
  };

  const getToolIcon = (toolName: string) => {
    switch (toolName) {
      case 'semantic_search':
        return <Search className="w-4 h-4" />;
      case 'keyword_search':
        return <FileText className="w-4 h-4" />;
      case 'expand_keyword_results':
      case 'expand_semantic_results':
        return <BookOpen className="w-4 h-4" />;
      case 'web_search':
      case 'web_search_tool':
        return <Globe className="w-4 h-4" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  const getToolLabel = (toolName: string) => {
    const labels: Record<string, string> = {
      'semantic_search': 'Searching documents',
      'keyword_search': 'Keyword search',
      'expand_keyword_results': 'Expanding keywords',
      'expand_semantic_results': 'Expanding context',
      'web_search': 'Web search',
      'web_search_tool': 'Web search',
    };
    return labels[toolName] || toolName.replace(/_/g, ' ');
  };


  const getStatusColor = () => {
    switch (call.status) {
      case 'running':
        return 'text-blue-500';
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-500';
      default:
        return 'text-blue-500';
    }
  };

  return (
    <div className={`flex items-center gap-2 text-xs ${getStatusColor()}`}>
      {getStatusIcon()}
      {getToolIcon(call.tool)}
      <span>{getToolLabel(call.tool)}</span>
      
      {call.status === 'completed' && call.resultCount !== undefined && (
        <span className="text-muted-foreground">
          ({call.resultCount} {call.resultCount === 1 ? 'result' : 'results'})
        </span>
      )}
      
      {call.status === 'completed' && call.duration && (
        <span className="text-muted-foreground">
          {(call.duration / 1000).toFixed(1)}s
        </span>
      )}
    </div>
  );
};

export default LiveToolCallStream;